import * as THREE from 'three';
import { getSharedStandardMaterial } from '../../../../utils/threeResourcePool';
import { getSilverBrushedAluminumMaps } from '../../../../utils/equipmentCabinetTextures';
import { FAB_COLORS, FAB_LED_INTENSITY } from '../rendering/fabColors';
import {
  createFabLightBluePhysicalGlass,
  createFabLightBlueSidePhysicalGlass,
  createFabLightBlueDistantGlass,
  createFabLightBlueSideDistantGlass,
  createFabSmokedPhysicalGlass,
  createFabSmokedDistantGlass,
  createFabGrayPhysicalGlass,
  createFabGrayDistantGlass,
} from '../rendering/fabGlassMaterials';

export const STOCKER_PALETTE = {
  silver: '#D8DEE4',
  brushedSilver: '#C5CDD6',
  whiteMetal: '#F0F2F5',
  graphite: FAB_COLORS.graphite,
  darkGraphite: FAB_COLORS.darkGraphite,
  ledNeutral: '#E2E8F0',
  ledNeutralGlow: '#CBD5E1',
  foupBody: '#2A3238',
  foupLid: '#4A5258',
  rubber: '#1A1E22',
} as const;

let brushedMaps: ReturnType<typeof getSilverBrushedAluminumMaps> | null = null;

function brushedMapsOnce() {
  if (!brushedMaps) brushedMaps = getSilverBrushedAluminumMaps();
  return brushedMaps;
}

export type StockerMaterialSet = {
  silverMetal: THREE.MeshStandardMaterial;
  brushedMetal: THREE.MeshStandardMaterial;
  darkMetal: THREE.MeshStandardMaterial;
  whiteMetal: THREE.MeshStandardMaterial;
  blueGlass: THREE.Material;
  blueGlassDistant: THREE.Material;
  sideGlass: THREE.Material;
  sideGlassDistant: THREE.Material;
  sideBlueGlass: THREE.Material;
  sideBlueGlassDistant: THREE.Material;
  topGrayGlass: THREE.Material;
  topGrayGlassDistant: THREE.Material;
  topBlueGlass: THREE.Material;
  topBlueGlassDistant: THREE.Material;
  navyBaseGlass: THREE.Material;
  navyBaseGlassDistant: THREE.Material;
  /** Dark smoked/black glass — back panel (+Z). */
  backBlackGlass: THREE.Material;
  backBlackGlassDistant: THREE.Material;
  cyanLed: THREE.MeshStandardMaterial;
  foupShell: THREE.MeshStandardMaterial;
  foupProcessedShell: THREE.MeshStandardMaterial;
  foupLid: THREE.MeshStandardMaterial;
  blackRubber: THREE.MeshStandardMaterial;
};

export function getStockerMaterials(opacity = 1, physicalGlass = true): StockerMaterialSet {
  const a = opacity;
  const maps = brushedMapsOnce();
  return {
    silverMetal: getSharedStandardMaterial({
      color: STOCKER_PALETTE.silver,
      metalness: 0.92,
      roughness: 0.28,
      envMapIntensity: 0.85,
      opacity: a,
      transparent: a < 1,
    }),
    brushedMetal: getSharedStandardMaterial({
      color: STOCKER_PALETTE.brushedSilver,
      map: maps.map,
      roughnessMap: maps.roughnessMap,
      metalness: 0.9,
      roughness: 0.32,
      envMapIntensity: 0.78,
      opacity: a,
      transparent: a < 1,
    }),
    darkMetal: getSharedStandardMaterial({
      color: STOCKER_PALETTE.graphite,
      metalness: 0.72,
      roughness: 0.28,
      envMapIntensity: 0.55,
      opacity: a,
      transparent: a < 1,
    }),
    whiteMetal: getSharedStandardMaterial({
      color: STOCKER_PALETTE.whiteMetal,
      metalness: 0.65,
      roughness: 0.22,
      envMapIntensity: 0.9,
      opacity: a,
      transparent: a < 1,
    }),
    blueGlass: physicalGlass
      ? createFabLightBluePhysicalGlass(a, 'fab-stocker-glass')
      : createFabLightBlueDistantGlass(a),
    blueGlassDistant: createFabLightBlueDistantGlass(a),
    sideGlass: physicalGlass
      ? createFabLightBlueSidePhysicalGlass(a, 'fab-stocker-side-glass')
      : createFabLightBlueSideDistantGlass(a),
    sideGlassDistant: createFabLightBlueSideDistantGlass(a),
    sideBlueGlass: physicalGlass
      ? createFabLightBluePhysicalGlass(a, 'fab-stocker-side-blue-glass')
      : createFabLightBlueDistantGlass(a),
    sideBlueGlassDistant: createFabLightBlueDistantGlass(a),
    topGrayGlass: physicalGlass
      ? createFabGrayPhysicalGlass(a, 'fab-stocker-top-gray-glass')
      : createFabGrayDistantGlass(a),
    topGrayGlassDistant: createFabGrayDistantGlass(a),
    topBlueGlass: physicalGlass
      ? createFabLightBluePhysicalGlass(a, 'fab-stocker-top-blue-glass')
      : createFabLightBlueDistantGlass(a),
    topBlueGlassDistant: createFabLightBlueDistantGlass(a),
    navyBaseGlass: physicalGlass
      ? createFabSmokedPhysicalGlass(a, 'fab-stocker-base-smoked')
      : createFabSmokedDistantGlass(a),
    navyBaseGlassDistant: createFabSmokedDistantGlass(a),
    backBlackGlass: physicalGlass
      ? createFabSmokedPhysicalGlass(a, 'fab-stocker-back-black-glass')
      : createFabSmokedDistantGlass(a),
    backBlackGlassDistant: createFabSmokedDistantGlass(a),
    cyanLed: getSharedStandardMaterial({
      color: STOCKER_PALETTE.ledNeutralGlow,
      emissive: STOCKER_PALETTE.ledNeutralGlow,
      emissiveIntensity: FAB_LED_INTENSITY.cyanEdge * 1.1,
      metalness: 0.05,
      roughness: 0.35,
      toneMapped: false,
      opacity: a,
      transparent: a < 1,
    }),
    foupShell: getSharedStandardMaterial({
      color: '#0B0D10',
      metalness: 0.28,
      roughness: 0.42,
      envMapIntensity: 0.6,
      opacity: a,
      transparent: a < 1,
    }),
    foupProcessedShell: getSharedStandardMaterial({
      color: '#0B0D10',
      metalness: 0.28,
      roughness: 0.42,
      envMapIntensity: 0.6,
      opacity: a,
      transparent: a < 1,
    }),
    foupLid: getSharedStandardMaterial({
      color: STOCKER_PALETTE.foupLid,
      metalness: 0.22,
      roughness: 0.36,
      envMapIntensity: 0.75,
      opacity: a,
      transparent: a < 1,
    }),
    blackRubber: getSharedStandardMaterial({
      color: STOCKER_PALETTE.rubber,
      metalness: 0.05,
      roughness: 0.88,
      opacity: a,
      transparent: a < 1,
    }),
  };
}
